import { forwardRef } from 'react';

/**
 * Botão do sistema.
 *
 * A variante `perigo` existe separada de `primario` porque várias ações aqui
 * são irreversíveis do ponto de vista de quem consulta o DNS — limpar o reverso
 * de um bloco ou remover uma zona reflete no mundo em segundos. Elas precisam
 * parecer diferentes antes do clique, não só depois.
 */

export type VarianteBotao = 'primario' | 'secundario' | 'fantasma' | 'perigo';
export type TamanhoBotao = 'normal' | 'tabela';

const VARIANTES: Record<VarianteBotao, string> = {
  primario:
    'bg-marca text-white hover:bg-marca-hover disabled:hover:bg-marca shadow-nav-ativo/0',
  secundario:
    'bg-superficie text-texto-medio border border-borda hover:border-marca hover:text-marca',
  fantasma: 'bg-transparent text-texto-suave hover:bg-borda-suave hover:text-texto',
  perigo: 'bg-erro-fundo text-erro border border-erro/20 hover:bg-erro hover:text-white',
};

const TAMANHOS: Record<TamanhoBotao, string> = {
  normal: 'h-11 px-4 text-[13.5px] rounded-controle',
  // Menor, para caber na altura da linha sem esticar a tabela.
  tabela: 'h-8 px-3 text-[12.5px] rounded-chip',
};

interface BotaoProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: VarianteBotao;
  tamanho?: TamanhoBotao;
  carregando?: boolean;
  iconeEsquerda?: React.ReactNode;
}

export const Botao = forwardRef<HTMLButtonElement, BotaoProps>(function Botao(
  {
    variante = 'secundario',
    tamanho = 'normal',
    carregando = false,
    iconeEsquerda,
    children,
    className = '',
    disabled,
    ...resto
  },
  ref,
) {
  return (
    <button
      ref={ref}
      // Desabilita durante o carregamento: sem isso, um duplo clique numa ação
      // de publicação dispara duas vezes o PATCH no PowerDNS.
      disabled={disabled || carregando}
      aria-busy={carregando || undefined}
      className={`inline-flex items-center justify-center gap-2 font-bold transition-colors
        disabled:cursor-not-allowed disabled:opacity-55 ${VARIANTES[variante]} ${
          TAMANHOS[tamanho]
        } ${className}`}
      {...resto}
    >
      {carregando ? (
        <span
          aria-hidden="true"
          className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      ) : (
        iconeEsquerda
      )}
      {children}
    </button>
  );
});
